import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Search, Loader2, Plus } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import { Client, Supplier } from '../../types';
import { supabase } from '../../lib/supabase';
import { useAppStore } from '../../store';
import ErrorModal from './ErrorModal';

interface EntityModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: 'client' | 'supplier';
  onSave: (entity: Client | Supplier) => void;
  initialData?: Client | Supplier;
}

const taxConditionOptions = [
  { value: 'RI', label: 'Responsable Inscripto' },
  { value: 'MT', label: 'Monotributista' },
  { value: 'EX', label: 'Exento' },
  { value: 'CF', label: 'Consumidor Final' },
  { value: 'NC', label: 'No Categorizado' },
];

const EntityModal: React.FC<EntityModalProps> = ({
  isOpen,
  onClose,
  entityType,
  onSave,
  initialData,
}) => {
  const { setError } = useAppStore();
  const [formData, setFormData] = useState({
    cuit: '',
    business_name: '',
    name: '',
    tax_condition: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    province: '',
    category: '',
    salesperson: '',
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [supplierCategories, setSupplierCategories] = useState<{id: string, name: string}[]>([]);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  
  // Fetch supplier categories
const fetchSupplierCategories = async () => {
  try {
    setIsLoadingCategories(true);
    const { data, error } = await supabase
      .from('supplier_categories')
      .select('id, name')
      .order('name');
    
    if (error) throw error;

    let updatedCategories = data || [];

    // Asegurarse que la categoría actual (si existe) esté en el listado
    if (
      formData.category &&
      !updatedCategories.some(cat => cat.name === formData.category)
    ) {
      updatedCategories = [
        ...updatedCategories,
        { id: 'custom', name: formData.category }
      ];
    }

    setSupplierCategories(updatedCategories);
  } catch (error) {
    console.error('Error fetching supplier categories:', error);
    setLocalError('Error al cargar las categorías de proveedores');
    setShowErrorModal(true);
  } finally {
    setIsLoadingCategories(false);
  }
};

  
  useEffect(() => {
    if (isOpen && entityType === 'supplier') {
      fetchSupplierCategories();
    }
  }, [isOpen, entityType]);
  
  useEffect(() => {
    if (initialData) {
      const entity = initialData as (Client | Supplier);
      setFormData({
        cuit: entity.cuit || '',
        business_name: entity.business_name || '',
        name: entity.name,
        tax_condition: entity.tax_condition || '',
        email: entity.email || '',
        phone: entity.phone || '',
        address: entity.address || '',
        city: entity.city || '',
        postal_code: entity.postal_code || '',
        province: entity.province || '',
        category: (entity as Supplier).category || '',
        salesperson: (entity as Client).salesperson || '',
      });
    } else {
      setFormData({
        cuit: '',
        business_name: '',
        name: '',
        tax_condition: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        postal_code: '',
        province: '',
        category: '',
        salesperson: '',
      });
    }
    setLocalError(null);
  }, [initialData, entityType, isOpen]);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    setLocalError(null);
  };

  const fetchAfipData = async () => {
    if (!formData.cuit) {
      setLocalError('Ingrese un CUIT');
      setShowErrorModal(true);
      return;
    }

    setIsLoading(true);
    setLocalError(null);

    try {
      const response = await fetch(
        `${supabase.supabaseUrl}/functions/v1/afip-padron?cuit=${formData.cuit}`,
        {
          headers: {
            Authorization: `Bearer ${supabase.supabaseKey}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Error al conectar con el servicio de AFIP');
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Error al consultar AFIP');
      }

      setFormData(prev => ({
        ...prev,
        business_name: data.data.nombre,
        name: data.data.nombre,
        address: data.data.domicilioFiscal.direccion,
        city: data.data.domicilioFiscal.localidad,
        postal_code: data.data.domicilioFiscal.codPostal,
        province: data.data.domicilioFiscal.descripcionProvincia,
        tax_condition: data.data.tipoContribuyente === 'RESPONSABLE INSCRIPTO' ? 'RI' : 'NC',
      }));
    } catch (error) {
      setLocalError(error.message);
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      setLocalError('El nombre de la categoría no puede estar vacío');
      setShowErrorModal(true);
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Insert new category
      const { data, error } = await supabase
        .from('supplier_categories')
        .insert([{ name: newCategoryName.trim() }])
        .select()
        .single();
      
      if (error) {
        if (error.code === '23505') { // Unique violation
          throw new Error('Ya existe una categoría con ese nombre');
        }
        throw error;
      }
      
      // Update categories list
      setSupplierCategories(prev => [...prev, data]);
      
      // Select the new category
      setFormData(prev => ({
        ...prev,
        category: data.name
      }));
      
      // Reset state
      setNewCategoryName('');
      setIsAddingCategory(false);
    } catch (error) {
      setLocalError(error.message);
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setLocalError('El nombre es obligatorio');
      setShowErrorModal(true);
      return;
    }

    // ⇢ NUEVO BLOQUE
    if (entityType === 'supplier' && !formData.category.trim()) {
      setLocalError('Seleccioná una categoría');
      setShowErrorModal(true);
      return;
    }

    try {
      let entity: any = {
        name: formData.name,
        email: formData.email || null,
        phone: formData.phone || null,
        address: formData.address || null,
        cuit: formData.cuit || null,
        business_name: formData.business_name || null,
        tax_condition: formData.tax_condition || null,
        city: formData.city || null,
        postal_code: formData.postal_code || null,
        province: formData.province || null,
      };
      // Add salesperson only for clients
      if (entityType === 'client') {
        entity.salesperson = formData.salesperson || null;
      }
      // Add ID only when editing an existing entity
      if (initialData) {
        entity.id = initialData.id;
        // Include balance only when it exists in initialData
        if (initialData.balance !== undefined) {
          entity.balance = initialData.balance;
        }
      }
      // Add category only for suppliers
      if (entityType === 'supplier') {
        entity = {
          ...entity,
          category: formData.category || null,
        };
      }
      await onSave(entity);
      onClose();
    } catch (error) {
      if (error.message.includes('duplicate key value')) {
        if (error.message.includes('_email_key')) {
          setLocalError(`Ya existe un ${entityType === 'client' ? 'cliente' : 'proveedor'} con ese email`);
        } else if (error.message.includes('_cuit_key')) {
          setLocalError(`Ya existe un ${entityType === 'client' ? 'cliente' : 'proveedor'} con ese CUIT`);
        } else {
          setLocalError('Ya existe un registro con esos datos');
        }
        setShowErrorModal(true);
      } else {
        setError(error.message);
      }
    }
  };
  
  return (
    <>
      <Dialog.Root open={isOpen} onOpenChange={onClose}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content className="fixed left-[50%] top-[50%] max-h-[85vh] w-[90vw] max-w-[600px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white dark:bg-gray-800 p-6 shadow-lg overflow-y-auto z-50">
            <Dialog.Title className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
              {initialData ? 'Editar' : 'Nuevo'} {entityType === 'client' ? 'Cliente' : 'Proveedor'}
            </Dialog.Title>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    CUIT
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id="cuit"
                      name="cuit"
                      value={formData.cuit}
                      onChange={handleInputChange}
                      placeholder="20-12345678-9"
                      className="flex-1 h-12 px-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={fetchAfipData}
                      disabled={isLoading}
                      className="h-12 flex items-center justify-center"
                    >
                      {isLoading ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <Search className="h-5 w-5" />
                      )}
                    </Button>
                  </div>
                </div>

                <Input
                  label="Razón Social"
                  id="business_name"
                  name="business_name"
                  value={formData.business_name}
                  onChange={handleInputChange}
                />

                <Select
                  label="Condición IVA"
                  value={formData.tax_condition}
                  onChange={(value) => setFormData(prev => ({ ...prev, tax_condition: value }))}
                  options={taxConditionOptions}
                />

                {entityType === 'supplier' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Categoría
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <Select
                          label=""
                          value={formData.category}
                          onChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                          required
                          options={[
                            { value: '', label: 'Seleccionar categoría' },
                            ...supplierCategories.map(cat => ({
                              value: cat.name,
                              label: cat.name,
                            }))
                          ]}
                        />
                      </div>
                      {isAddingCategory ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            placeholder="Nueva categoría"
                            className="h-12 px-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleAddCategory}
                            disabled={isLoading || !newCategoryName.trim()}
                            className="h-12"
                          >
                            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar'}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setIsAddingCategory(false);
                              setNewCategoryName('');
                            }}
                            className="h-12"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setIsAddingCategory(true)}
                          className="h-12 flex items-center justify-center"
                          icon={<Plus className="h-4 w-4" />}
                        >
                          Nueva categoría
                        </Button>
                      )}
                    </div>
                  </div>
                )}
                
                <Input
                  label="Nombre"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
                
                <Input
                  label="Email"
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                />
                
                <Input
                  label="Teléfono"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
                
                <Input
                  label="Dirección"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                />

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Ciudad"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                  />
                  
                  <Input
                    label="Código Postal"
                    id="postal_code"
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleInputChange}
                  />
                </div>

                <Input
                  label="Provincia"
                  id="province"
                  name="province"
                  value={formData.province}
                  onChange={handleInputChange}
                />
                {/* Campo vendedor solo para clientes */}
                {entityType === 'client' && (
                  <Select
                    label="Vendedor (opcional)"
                    value={formData.salesperson}
                    onChange={(value) => setFormData(prev => ({ ...prev, salesperson: value }))}
                    options={[
                      { value: '', label: 'Sin vendedor asignado' },
                      { value: 'Male', label: 'Male (malena@productex.com.ar)' },
                      { value: 'Mateo', label: 'Mateo (mateo@productex.com.ar)' },
                    ]}
                  />
                )}
              </div>
              
              <div className="mt-6 flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading}
                >
                  {initialData ? 'Guardar Cambios' : 'Guardar'}
                </Button>
              </div>
            </form>
            
            <Dialog.Close asChild>
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                aria-label="Close"
              >
                <X className="h-6 w-6" />
              </button>
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={localError || 'Ha ocurrido un error'}
      />
    </>
  );
};

export default EntityModal;